// TODO (GB-8345): update dashboard to use shared loader facade and service

import { Injectable, inject } from '@angular/core'
import { createEffect, Actions, ofType } from '@ngrx/effects'
import {
  switchMap,
  catchError,
  of,
  withLatestFrom,
  map,
  Observable,
  tap,
  mergeMap,
  filter,
  finalize,
  exhaustMap,
  zip,
} from 'rxjs'
import * as BaseEventTicketsActions from './base-event-tickets.actions'
import { BaseEventTicketsApiService } from './base-event-tickets-api.service'
import { gbCheckLoaded } from 'libs/shared/utils/operators/src/lib/gb-check-loaded/gb-check-loaded.operator'
import { BaseEventTicketsFacade } from './base-event-tickets.facade'
import { GbNotificationService } from 'libs/shared/utils/notification/src/lib/service/gb-notification.service'
import { Action } from '@ngrx/store'
import { BaseEventTicketEntity, CheckInGuestResultEnum } from './base-event-tickets.models'
import { GbErrorMessageService } from 'libs/shared/utils/helpers/src/lib/gb-error-message/gb-error-message.service'
import { HttpErrorResponse } from '@angular/common/http'
import { GbEventGuestScannerDialogComponent } from 'libs/shared/event-tickets/feature/src/lib/gb-event-guest-scanner-dialog/gb-event-guest-scanner-dialog.component'
import { GbBaseDialogService } from 'libs/shared/utils/dialogs/src/lib/gb-base-dialog-service/gb-base-dialog.service'
import {
  EventGuestScannerViewEnum,
  GbEventGuestScannerViewService,
} from 'libs/shared/event-tickets/feature/src/lib/gb-event-guest-scanner-dialog/gb-event-guest-scanner-view.service'
import * as BaseEventTicketCheckInsActions from 'libs/shared/event-tickets/data-access-event-ticket-check-ins/src/lib/+state/base-event-ticket-check-ins.actions'
import { EventTicketGuestStatusEnum } from 'libs/shared/event-tickets/data-access/src/lib/+state/base-event-tickets.models'
import { GbLoaderService } from 'libs/shared/utils/loader/src/lib/services/gb-loader.service'
import { LoaderActionTypeEnum } from 'libs/shared/utils/loader/src/lib/+state/loader.models'
import { GbEventTicketAssignDialogComponent } from 'libs/shared/event-tickets/feature/src/lib/gb-event-ticket-assign-dialog/gb-event-ticket-assign-dialog.component'
import { GbEventTicketEditGuestDialogComponent } from 'libs/shared/event-tickets/feature/src/lib/gb-event-ticket-edit-guest-dialog/gb-event-ticket-edit-guest-dialog.component'
import { VerifiedStateEnum } from 'libs/shared/models/shared.models'
import { GbEventTicketTransferDialogComponent } from 'libs/shared/event-tickets/feature/src/lib/gb-event-ticket-transfer-dialog/gb-event-ticket-transfer-dialog.component'
import { TranslateService } from '@ngx-translate/core'
import { GbEventTicketConfirmDialogComponent } from 'libs/shared/event-tickets/feature/src/lib/gb-event-ticket-confirm-dialog/gb-event-ticket-confirm-dialog.component'

@Injectable()
export class BaseEventTicketsEffects {
  protected readonly actions$ = inject(Actions)
  protected readonly gbNotificationService = inject(GbNotificationService)
  protected readonly gbLoaderService = inject(GbLoaderService)
  private readonly baseEventTicketsApiService = inject(BaseEventTicketsApiService)
  private readonly baseEventTicketsFacade = inject(BaseEventTicketsFacade)
  private readonly gbEventGuestScannerViewService = inject(GbEventGuestScannerViewService)
  private readonly gbErrorMessageService = inject(GbErrorMessageService)
  private readonly gbDialogService = inject(GbBaseDialogService)
  private readonly translateService = inject(TranslateService)

  loadEventTickets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseEventTicketsActions.loadEventTickets),
      gbCheckLoaded(this.auctionUrlName$),
      withLatestFrom(
        this.auctionUrlName$,
        this.baseEventTicketsFacade.limit$,
        this.baseEventTicketsFacade.offset$,
        this.baseEventTicketsFacade.filters$,
      ),
      switchMap(([, urlName, limit, offset, filters]) =>
        this.baseEventTicketsApiService.getEventTickets(urlName, limit, offset, filters).pipe(
          map((response) =>
            BaseEventTicketsActions.loadEventTicketsSuccess({
              eventTickets: response.results,
              count: response.count,
            }),
          ),
          catchError((error) => of(BaseEventTicketsActions.loadEventTicketsFailure({ error }))),
        ),
      ),
    ),
  )

  loadFilteredEventTickets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseEventTicketsActions.loadFilteredEventTickets),
      switchMap(() => [BaseEventTicketsActions.removeAllEventTickets(), BaseEventTicketsActions.loadEventTickets()]),
    ),
  )

  syncEventTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseEventTicketsActions.syncEventTicket),
      tap((action) => {
        if (action.showLoader) this.gbLoaderService.showLoaderDialog(LoaderActionTypeEnum.LoadEventTicket)
      }),
      withLatestFrom(this.auctionUrlName$),
      switchMap(([action, urlName]) =>
        this.baseEventTicketsApiService.getEventTicket(urlName, action.uuid, action.token).pipe(
          switchMap((eventTicket) => {
            const actions: Action[] = [BaseEventTicketsActions.syncEventTicketSuccess({ eventTicket })]
            if (action.onSuccess) actions.push(action.onSuccess())

            return actions
          }),
          catchError((error) =>
            action.onError ? of(action.onError(error)) : of(BaseEventTicketsActions.syncEventTicketFailure({ error })),
          ),
          finalize(() => this.gbLoaderService.hideLoaderDialog(LoaderActionTypeEnum.LoadEventTicket)),
        ),
      ),
    ),
  )

  loadEventGuestSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseEventTicketsActions.loadEventGuestSummary),
      gbCheckLoaded(this.auctionUrlName$),
      withLatestFrom(this.auctionUrlName$),
      switchMap(([, urlName]) =>
        this.baseEventTicketsApiService.getEventGuestSummary(urlName).pipe(
          map((response) => BaseEventTicketsActions.loadEventGuestSummarySuccess({ summary: response })),
          catchError((error) => of(BaseEventTicketsActions.loadEventGuestSummaryFailure({ error }))),
        ),
      ),
    ),
  )

  assignEventTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseEventTicketsActions.assignEventTicket),
      withLatestFrom(this.auctionUrlName$, this.baseEventTicketsFacade.selectedEventTicket$),
      tap(() => this.gbLoaderService.showLoaderDialog(LoaderActionTypeEnum.EventTicketAction)),
      mergeMap(([action, urlName, eventTicket]) =>
        this.baseEventTicketsApiService.assignEventTicket(urlName, action.payload, eventTicket?.uuid).pipe(
          map((response) => BaseEventTicketsActions.assignEventTicketSuccess({ eventTicket: response.event_ticket })),
          catchError((error) => of(BaseEventTicketsActions.assignEventTicketFailure({ error }))),
          finalize(() => this.gbLoaderService.hideLoaderDialog(LoaderActionTypeEnum.EventTicketAction)),
        ),
      ),
    ),
  )

  assignEventTicketSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BaseEventTicketsActions.assignEventTicketSuccess),
        tap(() => {
          this.gbDialogService.closeDialog(GbEventTicketAssignDialogComponent)
          this.gbNotificationService.success('SHARED.EVENT_TICKETS.ASSIGN_TICKET.SUCCESS_TOAST')
        }),
      ),
    { dispatch: false },
  )

  bulkAssignEventTickets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseEventTicketsActions.bulkAssignEventTickets),
      withLatestFrom(this.auctionUrlName$),
      tap(() => this.gbLoaderService.showLoaderDialog(LoaderActionTypeEnum.EventTicketAction)),
      mergeMap(([action, urlName]) =>
        this.baseEventTicketsApiService.bulkAssignEventTickets(urlName, action.payload).pipe(
          map(() => BaseEventTicketsActions.bulkAssignEventTicketsSuccess()),
          catchError((error) => of(BaseEventTicketsActions.bulkAssignEventTicketsFailure({ error }))),
          finalize(() => this.gbLoaderService.hideLoaderDialog(LoaderActionTypeEnum.EventTicketAction)),
        ),
      ),
    ),
  )

  bulkAssignEventTicketsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseEventTicketsActions.bulkAssignEventTicketsSuccess),
      map(() => BaseEventTicketsActions.finaliseEventTicketPurchaseSuccess()),
    ),
  )

  editEventTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseEventTicketsActions.editEventTicket),
      withLatestFrom(this.auctionUrlName$, this.baseEventTicketsFacade.selectedEventTicket$),
      tap(() => this.gbLoaderService.showLoaderDialog(LoaderActionTypeEnum.EventTicketAction)),
      mergeMap(([action, urlName, eventTicket]) =>
        this.baseEventTicketsApiService.editEventTicket(urlName, action.payload, eventTicket?.uuid).pipe(
          map((response) =>
            BaseEventTicketsActions.editEventTicketSuccess({
              eventTicket: response.event_ticket,
              isCheckIn: action.isCheckIn,
              onSuccess: action.onSuccess,
            }),
          ),
          catchError((error) => of(BaseEventTicketsActions.editEventTicketFailure({ error }))),
          finalize(() => this.gbLoaderService.hideLoaderDialog(LoaderActionTypeEnum.EventTicketAction)),
        ),
      ),
    ),
  )

  editEventTicketSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BaseEventTicketsActions.editEventTicketSuccess),
        tap((action) => {
          if (!action.isCheckIn) this.gbNotificationService.success('SHARED.EVENT_TICKETS.EDIT_GUEST.SUCCESS_TOAST')
          if (action.onSuccess) action.onSuccess()
        }),
      ),
    { dispatch: false },
  )

  transferEventTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseEventTicketsActions.transferEventTicket),
      withLatestFrom(this.auctionUrlName$, this.baseEventTicketsFacade.selectedEventTicket$),
      tap(() => this.gbLoaderService.showLoaderDialog(LoaderActionTypeEnum.EventTicketAction)),
      mergeMap(([action, urlName, eventTicket]) =>
        this.baseEventTicketsApiService.transferEventTicket(urlName, action.payload, eventTicket?.uuid).pipe(
          map((response) =>
            BaseEventTicketsActions.transferEventTicketSuccess({
              eventTicket: response.new_ticket,
              oldEventTicket: response.old_ticket,
            }),
          ),
          catchError((error) => of(BaseEventTicketsActions.transferEventTicketFailure({ error }))),
          finalize(() => this.gbLoaderService.hideLoaderDialog(LoaderActionTypeEnum.EventTicketAction)),
        ),
      ),
    ),
  )

  /**
   * Resends either the invite or ticket based on the ticket status.
   */
  sendEventTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseEventTicketsActions.sendEventTicket),
      withLatestFrom(this.auctionUrlName$, this.baseEventTicketsFacade.selectedEventTicket$),
      tap(() => this.gbLoaderService.showLoaderDialog(LoaderActionTypeEnum.EventTicketAction)),
      mergeMap(([, urlName, eventTicket]) =>
        this.baseEventTicketsApiService.sendEventTicket(urlName, eventTicket?.uuid).pipe(
          map(() => BaseEventTicketsActions.sendEventTicketSuccess()),
          catchError((error) => of(BaseEventTicketsActions.sendEventTicketFailure({ error }))),
          finalize(() => this.gbLoaderService.hideLoaderDialog(LoaderActionTypeEnum.EventTicketAction)),
        ),
      ),
    ),
  )

  showToastOnSendEventTicketSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BaseEventTicketsActions.sendEventTicketSuccess),
        tap(() => this.gbNotificationService.success('SHARED.EVENT_TICKETS.SEND_TICKET.SUCCESS_TOAST')),
      ),
    { dispatch: false },
  )

  transferEventTicketSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseEventTicketsActions.transferEventTicketSuccess),
      tap(() => {
        this.gbDialogService.closeDialog(GbEventTicketTransferDialogComponent)
        this.gbNotificationService.success('SHARED.EVENT_TICKETS.TRANSFER_TICKET.SUCCESS_TOAST')
      }),
      map((action) =>
        // remove old event ticket from state
        BaseEventTicketsActions.removeEventTicket({
          uuid: action.oldEventTicket.uuid,
        }),
      ),
    ),
  )

  startQRCodeCheckInProcess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseEventTicketsActions.startQRCodeCheckInProcess),
      tap(() => this.gbDialogService.closeDialog(GbEventGuestScannerDialogComponent)),
      map((action) =>
        BaseEventTicketsActions.syncEventTicket({
          uuid: action.uuid,
          showLoader: true,
          onSuccess: () =>
            BaseEventTicketsActions.startCheckInProcess({
              uuid: action.uuid,
              isQRCodeCheckIn: true,
              onComplete: (result) =>
                BaseEventTicketsActions.openEventGuestScannerDialog(EventGuestScannerViewEnum.Result, result),
            }),
        }),
      ),
    ),
  )

  startCheckInProcess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseEventTicketsActions.startCheckInProcess),
      gbCheckLoaded(this.baseEventTicketsFacade.selectedEventTicket$),
      withLatestFrom(this.baseEventTicketsFacade.selectedEventTicket$),
      map(([action, eventTicket]) => {
        const checkInGuestAction = BaseEventTicketsActions.checkInGuest(action)

        return this.requiresAdditionalCheckInInfo(eventTicket)
          ? BaseEventTicketsActions.openEventTicketEditGuestDialog({
              uuid: action.uuid,
              isCheckIn: true,
              onSuccess: () => checkInGuestAction,
            })
          : checkInGuestAction
      }),
    ),
  )

  checkInGuest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseEventTicketsActions.checkInGuest),
      withLatestFrom(this.auctionUrlName$),
      tap(([action]) => this.gbLoaderService.setLoading(action.uuid)),
      mergeMap(([action, urlName]) =>
        this.baseEventTicketsApiService.checkIn(urlName, action.uuid).pipe(
          map((response) =>
            BaseEventTicketsActions.checkInGuestSuccess({
              eventTicket: response.event_ticket,
              uuid: response.event_ticket.uuid,
              isQRCodeCheckIn: action.isQRCodeCheckIn,
              onComplete: action.onComplete,
            }),
          ),
          catchError((error) =>
            of(
              BaseEventTicketsActions.checkInGuestFailure({
                error,
                uuid: action.uuid,
                isQRCodeCheckIn: action.isQRCodeCheckIn,
                onComplete: action.onComplete,
              }),
            ),
          ),
          finalize(() => this.gbLoaderService.setLoadingComplete(action.uuid)),
        ),
      ),
    ),
  )

  checkInGuestSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BaseEventTicketsActions.checkInGuestSuccess),
        filter((action) => !action.isQRCodeCheckIn),
        tap(() => this.gbNotificationService.success('SHARED.EVENT_TICKETS.CHECK_IN.SUCCESS_TOAST_CHECK_IN')),
      ),
    { dispatch: false },
  )

  checkInGuestFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BaseEventTicketsActions.checkInGuestFailure),
        filter((action) => !action.isQRCodeCheckIn),
        tap((action) => this.showErrorToast(action.error)),
      ),
    { dispatch: false },
  )

  checkInGuestOnComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseEventTicketsActions.checkInGuestSuccess, BaseEventTicketsActions.checkInGuestFailure),
      filter((action) => !!action.onComplete),
      withLatestFrom(this.baseEventTicketsFacade.selectedEventTicket$),
      switchMap(([action, eventTicket]) => {
        const actions: Action[] = []

        const result =
          action.type === BaseEventTicketsActions.checkInGuestSuccess.type
            ? CheckInGuestResultEnum.Success
            : CheckInGuestResultEnum.Failure

        if (action.onComplete) actions.push(action.onComplete(result))

        if (
          action.isQRCodeCheckIn &&
          (result === CheckInGuestResultEnum.Success || // check-in success
            eventTicket?.guest_status === EventTicketGuestStatusEnum.CheckedIn) // already checked-in
        ) {
          actions.push(BaseEventTicketCheckInsActions.syncSelectedEventTicketCheckIn())
        }

        return actions
      }),
    ),
  )

  checkOutGuest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseEventTicketsActions.checkOutGuest),
      withLatestFrom(this.auctionUrlName$),
      tap(([action]) => this.gbLoaderService.setLoading(action.uuid)),
      mergeMap(([action, urlName]) =>
        this.baseEventTicketsApiService.checkOut(urlName, action.uuid).pipe(
          map((response) =>
            BaseEventTicketsActions.checkOutGuestSuccess({
              eventTicket: response.event_ticket,
              uuid: response.event_ticket.uuid,
            }),
          ),
          catchError((error) => of(BaseEventTicketsActions.checkOutGuestFailure({ error, uuid: action.uuid }))),
          finalize(() => this.gbLoaderService.setLoadingComplete(action.uuid)),
        ),
      ),
    ),
  )

  checkOutGuestSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BaseEventTicketsActions.checkOutGuestSuccess),
        tap(() => this.gbNotificationService.success('SHARED.EVENT_TICKETS.CHECK_IN.SUCCESS_TOAST_CHECK_OUT')),
      ),
    { dispatch: false },
  )

  upsertEventTicketOnSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        BaseEventTicketsActions.upsertEventTicketOnSuccess,
        BaseEventTicketsActions.syncEventTicketSuccess,
        BaseEventTicketsActions.assignEventTicketSuccess,
        BaseEventTicketsActions.editEventTicketSuccess,
        BaseEventTicketsActions.transferEventTicketSuccess,
        BaseEventTicketsActions.checkInGuestSuccess,
        BaseEventTicketsActions.checkOutGuestSuccess,
      ),
      withLatestFrom(this.baseEventTicketsFacade.allEventTickets$),
      map(([action, eventTickets]) => {
        const notListLoaded = eventTickets.every(
          (eventTicket) => eventTicket.uuid !== action.eventTicket.uuid || eventTicket.notListLoaded,
        )

        // add notListLoaded = true if ticket is not in list
        return BaseEventTicketsActions.upsertEventTicket({
          eventTicket: { ...action.eventTicket, notListLoaded },
        })
      }),
    ),
  )

  showErrorToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          BaseEventTicketsActions.assignEventTicketFailure,
          BaseEventTicketsActions.bulkAssignEventTicketsFailure,
          BaseEventTicketsActions.editEventTicketFailure,
          BaseEventTicketsActions.transferEventTicketFailure,
          BaseEventTicketsActions.sendEventTicketFailure,
          BaseEventTicketsActions.checkOutGuestFailure,
        ),
        tap((action) => this.showErrorToast(action.error)),
      ),
    { dispatch: false },
  )

  /** DIALOG EFFECTS **/

  openEventTicketAssignDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseEventTicketsActions.openEventTicketAssignDialog),
      switchMap(() =>
        this.gbDialogService
          .getDialogObs(GbEventTicketAssignDialogComponent, { disableClose: true })
          .pipe(map(() => BaseEventTicketsActions.clearSelectedEventTicket())),
      ),
    ),
  )

  openEventTicketEditGuestDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseEventTicketsActions.openEventTicketEditGuestDialog),
      exhaustMap((action) =>
        zip([
          of(action),
          this.gbDialogService.getDialogObs(GbEventTicketEditGuestDialogComponent, {
            disableClose: true,
            data: { isCheckIn: action.isCheckIn },
          }),
        ]),
      ),
      switchMap(([action, result]) => {
        const actions: Action[] = [BaseEventTicketsActions.clearSelectedEventTicket()]
        if (result && action.onSuccess) actions.push(action.onSuccess())

        return actions
      }),
    ),
  )

  openResendDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseEventTicketsActions.openResendInviteDialog, BaseEventTicketsActions.openResendTicketDialog),
      withLatestFrom(this.baseEventTicketsFacade.selectedEventTicket$),
      switchMap(([action, eventTicket]) => {
        const headerKey =
          action.type === BaseEventTicketsActions.openResendInviteDialog.type
            ? 'SHARED.EVENT_TICKETS.CONFIRM.RESEND_INVITE_HEADER'
            : 'SHARED.EVENT_TICKETS.CONFIRM.RESEND_TICKET_HEADER'

        const dialogData = {
          mobileFullScreen: false,
          data: {
            header: headerKey,
            description: this.translateService.instant('SHARED.EVENT_TICKETS.CONFIRM.RESEND_DESCRIPTION', {
              email: eventTicket?.profile?.email,
            }),
            confirmButtonText: 'CONFIRM',
            cancelButtonText: 'CANCEL',
          },
        }

        return this.gbDialogService.getDialogObs(GbEventTicketConfirmDialogComponent, dialogData).pipe(
          filter((result) => !!result),
          map(() => BaseEventTicketsActions.sendEventTicket()),
        )
      }),
    ),
  )

  openEventTicketTransferDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseEventTicketsActions.openEventTicketTransferDialog),
      switchMap(() =>
        this.gbDialogService
          .getDialogObs(GbEventTicketTransferDialogComponent, { disableClose: true })
          .pipe(map(() => BaseEventTicketsActions.clearSelectedEventTicket())),
      ),
    ),
  )

  openEventGuestScannerDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BaseEventTicketsActions.openEventGuestScannerDialog),
        tap((action) => {
          if (action.view) this.gbEventGuestScannerViewService.setViewShowing(action.view)
        }),
        mergeMap((action) =>
          this.gbDialogService
            .getDialogObs(GbEventGuestScannerDialogComponent, {
              width: '460px',
              data: { result: action.result },
            })
            .pipe(
              switchMap(() => [
                BaseEventTicketsActions.clearSelectedEventTicket(),
                BaseEventTicketsActions.removeNotListLoadedEventTickets(),
                BaseEventTicketCheckInsActions.clearSelectedEventTicketCheckIn(),
              ]),
            ),
        ),
      ),
    { dispatch: false },
  )

  removeNotListLoadedEventTickets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BaseEventTicketsActions.removeNotListLoadedEventTickets),
      withLatestFrom(this.baseEventTicketsFacade.allEventTickets$),
      switchMap(([, eventTickets]) => {
        const actions: Action[] = []

        const uuids = eventTickets
          .filter((eventTicket) => eventTicket.notListLoaded)
          .map((eventTicket) => eventTicket.uuid)

        if (uuids.length > 0) {
          actions.push(BaseEventTicketsActions.removeManyEventTickets({ uuids }))
        }

        return actions
      }),
    ),
  )

  constructor(private readonly auctionUrlName$: Observable<string | undefined>) {}

  protected showErrorToast(error: HttpErrorResponse) {
    const errorMessage = this.gbErrorMessageService.getErrorMessage(error['error'])
    this.gbNotificationService.error(errorMessage || 'SHARED.ERROR.GENERIC')
  }

  /**
   * Checks if dialog should be shown on check-in when:
   * - Ticket has valid status for check-in (invite sent, accepted, complete)
   * - One or more required custom fields are incomplete
   * - Guest has unverified email
   */
  private requiresAdditionalCheckInInfo(eventTicket?: BaseEventTicketEntity | null): boolean {
    return (
      this.isValidStatusForCheckIn(eventTicket) &&
      (!eventTicket?.profile?.required_custom_fields_complete ||
        eventTicket?.profile?.email_verified_state !== VerifiedStateEnum.Verified)
    )
  }

  /**
   * Checks if the event ticket status is valid for check-in.
   * Tickets which have been declined, cancelled or already checked-in
   * should not be prompted for additional info and instead show an error.
   * @returns
   */
  private isValidStatusForCheckIn(eventTicket?: BaseEventTicketEntity | null): boolean {
    if (!eventTicket?.guest_status) return false
    return [
      EventTicketGuestStatusEnum.InviteSent,
      EventTicketGuestStatusEnum.Accepted,
      EventTicketGuestStatusEnum.Complete,
    ].includes(eventTicket.guest_status)
  }
}
